<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

interface Props {
  /**
   * Button type
   * - 'sign-in': "Sign in with Google"
   * - 'continue': "Continue with Google"
   * - 'sign-up': "Sign up with Google"
   */
  type?: 'sign-in' | 'continue' | 'sign-up';

  /**
   * Corner radius style
   * - 'default': Standard rounded corners
   * - 'square': 90-degree corners
   * - 'capsule': Maximum rounded (pill shape)
   */
  cornerRadius?: 'default' | 'square' | 'capsule';

  /**
   * Button theme following Google's branding guidelines
   * - 'standard': Blue background (Google brand color)
   * - 'outline': White background with border
   */
  theme?: 'standard' | 'outline';

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Full width button
   */
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'sign-in',
  cornerRadius: 'default',
  theme: 'outline',
  loading: false,
  disabled: false,
  fullWidth: true,
});

const emit = defineEmits<{
  click: [];
}>();

const buttonText = computed(() => {
  switch (props.type) {
    case 'continue':
      return t('auth.registration.continueWithGoogle', 'Continue with Google');
    case 'sign-up':
      return t('auth.registration.signUpWithGoogle', 'Sign up with Google');
    case 'sign-in':
    default:
      return t('auth.login.signInWithGoogle', 'Sign in with Google');
  }
});

const cornerRadiusClass = computed(() => {
  switch (props.cornerRadius) {
    case 'square':
      return 'rounded-none';
    case 'capsule':
      return 'rounded-full';
    case 'default':
    default:
      return 'rounded-md';
  }
});

const themeClasses = computed(() => {
  if (props.theme === 'standard') {
    return 'bg-[#4285f4] text-white hover:bg-[#357ae8] border border-[#4285f4]';
  }
  return 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50';
});

const handleClick = () => {
  if (!props.loading && !props.disabled) {
    emit('click');
  }
};
</script>

<template>
  <button
    type="button"
    :class="[
      'google-signin-button',
      'flex items-center justify-center gap-2',
      'px-4 py-3',
      'font-roboto text-base font-medium',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      cornerRadiusClass,
      themeClasses,
      {
        'w-72': fullWidth,
        'min-w-[140px]': !fullWidth,
        'cursor-not-allowed opacity-60': disabled || loading,
        'focus:ring-blue-500': theme === 'standard',
        'focus:ring-gray-400': theme === 'outline',
      },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <!-- Google Icon SVG (official Google "G" logo) -->
    <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" class="flex-shrink-0">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </svg>

    <!-- Loading Spinner -->
    <svg v-else class="h-5 w-5 flex-shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>

    <span class="flex-1 text-center">{{ buttonText }}</span>
  </button>
</template>

<style scoped>
/* Ensure minimum height and consistent sizing with Apple button */
.google-signin-button {
  min-height: 44px;
  height: 44px;
}

/* Ensure minimum margin for consistency */
.google-signin-button {
  margin: 4px 0;
}
</style>
